import dayjs from 'dayjs';

import { DateService, get, IntlManager, isEmptyCollection } from '@atlas/core';

import { BASE_PATH } from 'common/constants';

import { capitalize } from './capitalize';
import { mapValues } from './mapValues';

export const CDN_TYPE_GAME = 'game';
export const CDN_TYPE_CMS = 'cms';
export const CDN_TYPE_SPORT = 'sport';
export const CDN_TYPE_CRM = 'crm';
export const CDN_TYPE_BASE = 'base';
export const CDN_TYPE_BILLING = 'billing';

const devCdnUrls = {
  [CDN_TYPE_BASE]: 'https://media.pl-test.cdn-platform.xyz',
  [CDN_TYPE_BILLING]: 'https://media.pl-test.cdn-platform.xyz/billing',
  [CDN_TYPE_CMS]: 'https://media.pl-test.cdn-platform.xyz/cms',
  [CDN_TYPE_CRM]: 'https://media.pl-test.cdn-platform.xyz/crm',
  [CDN_TYPE_GAME]: 'https://media.pl-test.cdn-platform.xyz/games',
  [CDN_TYPE_SPORT]: 'https://media.pl-test.cdn-platform.xyz/sport',
};

const stageCdnUrls = {
  [CDN_TYPE_BASE]: 'https://media.pl-stage1.cdn-platform.xyz',
  [CDN_TYPE_BILLING]: 'https://media.pl-stage1.cdn-platform.xyz/billing',
  [CDN_TYPE_CMS]: 'https://media.pl-stage1.cdn-platform.xyz/cms',
  [CDN_TYPE_CRM]: 'https://media.pl-stage1.cdn-platform.xyz/crm',
  [CDN_TYPE_GAME]: 'https://media.pl-stage1.cdn-platform.xyz/games',
  [CDN_TYPE_SPORT]: 'https://media.pl-stage1.cdn-platform.xyz/sport',
};

const prodCdnUrls = {
  [CDN_TYPE_BASE]: 'https://media.pl-01.cdn-platform.xyz',
  [CDN_TYPE_BILLING]: 'https://media.pl-01.cdn-platform.xyz/billing',
  [CDN_TYPE_CMS]: 'https://media.pl-01.cdn-platform.xyz/cms',
  [CDN_TYPE_CRM]: 'https://media.pl-01.cdn-platform.xyz/crm',
  [CDN_TYPE_GAME]: 'https://media.pl-01.cdn-platform.xyz/games',
  [CDN_TYPE_SPORT]: 'https://media.pl-01.cdn-platform.xyz/sport',
};

const getEnvCDN = () => {
  switch (process.env.WEBPACK_PROXY) {
    case 'stage1':
      return stageCdnUrls;
    case 'prod':
      return prodCdnUrls;
    default:
      return devCdnUrls;
  }
};

window.SERVICE_CDN = window.SERVICE_CDN || getEnvCDN();

const trimSlashStart = (str = '') => str.replace(/^\/+/g, '');

export const imageUrlCDN = (url, cdnType) => {
  const cdn = window.SERVICE_CDN[cdnType];
  const res = url || '';
  return [cdn, trimSlashStart(res)].join('/');
};

export const getCDNUrl = cdnType => {
  return window.SERVICE_CDN[cdnType];
};

export const getReadableString = string => capitalize(string.split('_').join(' '));

export const getAvailablePages = (availablePages, pagesToMatch) => {
  const tabs = [];

  pagesToMatch.forEach(item => {
    const { isActive, type } = item;
    const page = availablePages[type];

    if (page && isActive) {
      tabs.push({
        ...item,
        ...page,
      });
    }
  });

  return tabs;
};

export const getFieldError = field => {
  const { errors, touched } = field;

  if (!touched || !errors.length) {
    return null;
  }

  return get(errors, '[0].message') || true;
};

export const getMainMarketFromMarketType = marketType => {
  for (const market of Object.values(marketType.markets)) {
    if (market.isMain) {
      return market;
    }
  }

  return {};
};

export const getIsFieldRequired = rules => Boolean(rules.find(({ type }) => type === 'REQUIRED'));

// responsible gaming limits
export const getLimitWarning = (data, type) => {
  if (!data) {
    return null;
  }

  const { dateFrom, value } = data;

  return {
    message: value ? `${type}_limits_were_edited` : 'limits_edit_nullify',
    params: {
      date: DateService.getFormattedDateWithTime(dateFrom),
      value: value && IntlManager.formatMoney(value),
    },
  };
};

export const getDayStart = date => (dayjs(date).isValid() ? date.startOf('day').valueOf() : null);

export const getDayEnd = date => (dayjs(date).isValid() ? date.endOf('day').valueOf() : null);

export const loadScript = src =>
  new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.onload = resolve;
    script.onerror = reject;
    script.src = src;
    document.head.append(script);
  });

function getConfig(name, defaultValue = null) {
  return get(window.SYS_CONFIG, name, defaultValue);
}

export class LocalConfig {
  static get(name, defaultValue = null) {
    return getConfig(name, defaultValue);
  }

  static is(name, right) {
    const left = getConfig(name);
    return left === right;
  }
}

export const getSelfRegulationLimitsFromFields = (fields, spentType) =>
  Object.entries(fields).reduce((acc, [key, value]) => {
    const parsedValue = Number.parseFloat(value) || null;

    if (!Number.isFinite(parsedValue)) {
      const item = {
        type: key.toUpperCase(),
        value: 0,
      };

      if (spentType) {
        item.spentType = spentType;
      }

      return [...acc, item];
    }

    const item = {
      type: key.toUpperCase(),
      value: parsedValue,
    };

    if (spentType) {
      item.spentType = spentType;
    }

    return [...acc, item];
  }, []);

export const getSelfRegulationFormattedFields = (spentType, formPayload) => {
  const limits = getSelfRegulationLimitsFromFields(formPayload, spentType);

  if (isEmptyCollection(limits)) {
    return null;
  }

  return { limits };
};

export const getParsedFormErrorParameters = params => {
  return mapValues(params, (value, key) => {
    switch (key) {
      case 'limit': {
        return IntlManager.formatMoney(value);
      }
      default: {
        return value;
      }
    }
  });
};

export const base64ToHtmlURL = base64String => {
  const byteArray = Uint8Array.from(
    window
      .atob(base64String)
      .split('')
      .map(char => char.charCodeAt(0)),
  );

  return URL.createObjectURL(new Blob([byteArray], { type: 'text/html' }));
};

const uriPattern = /^https?:\/\//i;
export const isUrlAbsolute = url => uriPattern.test(url);

export const generatePaymentFormLink = paymentData =>
  `${BASE_PATH}/static/assets/pages/payment-form.html?` +
  `redirectUrl=${encodeURIComponent(paymentData.redirectUrl)}&` +
  `formData=${encodeURIComponent(JSON.stringify(paymentData.formData))}`;

export const get1X2Outcomes = outcomes => ({
  awayOdds: outcomes.find(outcome => outcome.type === '{participant2}')?.odds,
  drawOdds: outcomes.find(outcome => outcome.type === 'X')?.odds,
  homeOdds: outcomes.find(outcome => outcome.type === '{participant1}')?.odds,
});

export const isString = value => typeof value === 'string';

export const isNumber = value => typeof value === 'number';

export const uniq = array => [...new Set(array)];

/**
 * @param path {String}
 * @returns {string}
 */
export function normalizePath(path) {
  return path.replace(/\/+/g, '/').replace(/\/$/, '');
}

export const isLocalEnv = window.SYS_CONFIG.env === 'local';

export const isCasinoGamePage = (pathname, paths) =>
  paths.some(path => pathname.includes(`${path}/game`));

export const transformParameters = parametersArray => {
  const result = {};

  parametersArray.forEach(paramObj => {
    const { parameterName, possibleValues } = paramObj;

    result[parameterName] = Object.entries(possibleValues).map(([value, text]) => ({
      text,
      value,
    }));
  });

  return result;
};
